'use client';

import { useState } from 'react';
import { ChevronDown, ChevronUp, Info, GraduationCap, Target, Sprout, BookOpen, Baby, Footprints, Globe2, Microscope } from 'lucide-react';
import LearningStoryCard from './LearningStoryCard';
import ProgressTracker from './ProgressTracker';
import type { UserProgress, LevelInfo } from '@/lib/learning-progress';

interface AudioBook {
  id: string;
  title: string;
  prompt: string;
  coverImageUrl?: string;
  language: string;
  targetLanguage?: string;
  difficultyLevel: number;
  learningTopic?: string;
  ageCategory: string;
}

interface AgeCategorySectionProps {
  ageCategory: string;
  title: string;
  description: string;
  icon: string;
  stories: AudioBook[];
  userProgress?: UserProgress;
  levelInfo?: LevelInfo;
  completionStatus?: Record<string, boolean>;
  montessoriInfo?: {
    sensitivePeriods: string[];
    developmentalFocus: string[];
  };
  isExpanded?: boolean;
  onToggle?: () => void;
}

// Helper function to get Lucide icon component by name
const getAgeIcon = (iconName: string) => {
  const iconProps = { size: 48, color: 'white', strokeWidth: 2.5 };
  
  switch(iconName) {
    case 'Baby': return <Baby {...iconProps} />;
    case 'Footprints': return <Footprints {...iconProps} />;
    case 'Sprout': return <Sprout {...iconProps} />;
    case 'BookOpen': return <BookOpen {...iconProps} />;
    case 'Globe2': return <Globe2 {...iconProps} />;
    case 'Microscope': return <Microscope {...iconProps} />;
    default: return <BookOpen {...iconProps} />;
  }
};

export default function AgeCategorySection({
  ageCategory,
  title,
  description,
  icon,
  stories,
  userProgress,
  levelInfo,
  completionStatus = {},
  montessoriInfo,
  isExpanded = true,
  onToggle,
}: AgeCategorySectionProps) {
  const [showMontessoriInfo, setShowMontessoriInfo] = useState(false);

  return (
    <div
      style={{
        marginBottom: 'clamp(3rem, 5vw, 4rem)',
        background: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        borderRadius: '32px',
        padding: 'clamp(2rem, 4vw, 3rem) clamp(1.5rem, 3vw, 2rem)',
        border: '2px solid rgba(255, 255, 255, 0.8)',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(255, 255, 255, 0.5) inset',
        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
      }}
    >
      {/* Section Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          marginBottom: isExpanded ? '24px' : '0',
          flexWrap: 'wrap',
          gap: '16px',
          cursor: onToggle ? 'pointer' : 'default',
        }}
        onClick={onToggle}
      >
        <div style={{ flex: 1, minWidth: 'clamp(200px, 50vw, 280px)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'clamp(1rem, 3vw, 1.5rem)', marginBottom: 'clamp(1rem, 2vw, 1.25rem)' }}>
            <div
              style={{
                fontSize: 'clamp(2.5rem, 6vw, 3.5rem)',
                width: 'clamp(80px, 12vw, 100px)',
                height: 'clamp(80px, 12vw, 100px)',
                borderRadius: '24px',
                background: 'linear-gradient(135deg, #C9B6E4 0%, #A8D8E6 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 12px 40px rgba(201, 182, 228, 0.3)',
                transform: 'rotate(-3deg)',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
              className="age-icon"
            >
              {getAgeIcon(icon)}
            </div>
            <div>
              <h2
                style={{
                  background: 'linear-gradient(135deg, #B4A0E5, #7B68EE, #A8D8E6)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  fontSize: 'clamp(1.5rem, 4vw, 2.25rem)',
                  fontWeight: 900,
                  margin: '0 0 clamp(0.375rem, 1vw, 0.5rem) 0',
                  lineHeight: 1.2,
                  letterSpacing: '-0.01em',
                }}
              >
                {title}
              </h2>
              <p
                style={{
                  color: '#64748B',
                  fontSize: 'clamp(0.9375rem, 2vw, 1.125rem)',
                  margin: 0,
                  fontWeight: 600,
                  lineHeight: 1.5,
                }}
              >
                {description}
              </p>
            </div>
          </div>

          {/* Montessori Info Toggle - Enhanced */}
          {montessoriInfo && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowMontessoriInfo(!showMontessoriInfo);
              }}
              style={{
                background: 'linear-gradient(135deg, #F0F9FF, #E0F2FE)',
                border: '2px solid rgba(168, 216, 230, 0.4)',
                borderRadius: '12px',
                padding: 'clamp(0.625rem, 2vw, 0.75rem) clamp(1rem, 2vw, 1.25rem)',
                color: '#1E40AF',
                fontSize: 'clamp(0.875rem, 2vw, 0.9375rem)',
                fontWeight: 700,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 'clamp(0.5rem, 1vw, 0.625rem)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                boxShadow: '0 2px 8px rgba(168, 216, 230, 0.2)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'linear-gradient(135deg, #E0F2FE, #BAE6FD)';
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 16px rgba(168, 216, 230, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'linear-gradient(135deg, #F0F9FF, #E0F2FE)';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(168, 216, 230, 0.2)';
              }}
            >
              <div style={{
                background: 'linear-gradient(135deg, #10b981, #059669)',
                borderRadius: '50%',
                width: '20px',
                height: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <GraduationCap size={12} color="white" strokeWidth={2.5} />
              </div>
              Montessori Insights
              {showMontessoriInfo ? <ChevronUp size={18} strokeWidth={2.5} /> : <ChevronDown size={18} strokeWidth={2.5} />}
            </button>
          )}

          {/* Montessori Info Panel - Enhanced */}
          {showMontessoriInfo && montessoriInfo && (
            <div
              style={{
                marginTop: 'clamp(1rem, 2vw, 1.25rem)',
                background: 'linear-gradient(135deg, #FFF5EB 0%, #FEF3E2 100%)',
                borderRadius: '20px',
                padding: 'clamp(1.25rem, 3vw, 1.75rem)',
                border: '2px solid rgba(248, 200, 160, 0.4)',
                boxShadow: '0 4px 16px rgba(248, 200, 160, 0.2)',
              }}
            >
              <div style={{ marginBottom: 'clamp(1rem, 2vw, 1.25rem)' }}>
                <h4
                  style={{
                    color: '#92400E',
                    fontSize: 'clamp(0.9375rem, 2vw, 1.0625rem)',
                    fontWeight: 800,
                    margin: '0 0 clamp(0.625rem, 1.5vw, 0.75rem) 0',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                  }}
                >
                  <div style={{
                    background: 'linear-gradient(135deg, #10b981, #059669)',
                    borderRadius: '50%',
                    width: '24px',
                    height: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    <Sprout size={14} color="white" strokeWidth={2.5} />
                  </div>
                  Sensitive Periods:
                </h4>
                <ul style={{ margin: 0, paddingLeft: 'clamp(1.25rem, 3vw, 1.5rem)', color: '#78350F', fontSize: 'clamp(0.875rem, 2vw, 0.9375rem)', lineHeight: 1.7 }}>
                  {montessoriInfo.sensitivePeriods.map((period, idx) => (
                    <li key={idx} style={{ marginBottom: 'clamp(0.375rem, 1vw, 0.5rem)' }}>{period}</li>
                  ))}
                </ul>
              </div>
              <div>
                <h4
                  style={{
                    color: '#92400E',
                    fontSize: 'clamp(0.9375rem, 2vw, 1.0625rem)',
                    fontWeight: 800,
                    margin: '0 0 clamp(0.625rem, 1.5vw, 0.75rem) 0',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                  }}
                >
                  <div style={{
                    background: 'linear-gradient(135deg, #3B82F6, #2563EB)',
                    borderRadius: '50%',
                    width: '24px',
                    height: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    <Target size={14} color="white" strokeWidth={2.5} />
                  </div>
                  Developmental Focus:
                </h4>
                <ul style={{ margin: 0, paddingLeft: 'clamp(1.25rem, 3vw, 1.5rem)', color: '#78350F', fontSize: 'clamp(0.875rem, 2vw, 0.9375rem)', lineHeight: 1.7 }}>
                  {montessoriInfo.developmentalFocus.map((focus, idx) => (
                    <li key={idx} style={{ marginBottom: 'clamp(0.375rem, 1vw, 0.5rem)' }}>{focus}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>

        {/* Progress Tracker */}
        {userProgress && levelInfo && (
          <div style={{ minWidth: '300px', maxWidth: '400px', flex: '1' }}>
            <ProgressTracker progress={userProgress} levelInfo={levelInfo} showDetails={false} />
          </div>
        )}

        {/* Toggle Icon - Enhanced */}
        {onToggle && (
          <button
            style={{
              background: 'linear-gradient(135deg, #F8C8A0, #F7B68C)',
              border: '2px solid rgba(255, 255, 255, 0.8)',
              borderRadius: '50%',
              width: 'clamp(48px, 8vw, 60px)',
              height: 'clamp(48px, 8vw, 60px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              boxShadow: '0 4px 16px rgba(248, 200, 160, 0.3)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'linear-gradient(135deg, #F7B68C, #F8C8A0)';
              e.currentTarget.style.transform = 'scale(1.1) rotate(5deg)';
              e.currentTarget.style.boxShadow = '0 8px 24px rgba(248, 200, 160, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'linear-gradient(135deg, #F8C8A0, #F7B68C)';
              e.currentTarget.style.transform = 'scale(1) rotate(0deg)';
              e.currentTarget.style.boxShadow = '0 4px 16px rgba(248, 200, 160, 0.3)';
            }}
          >
            {isExpanded ? <ChevronUp size={28} color="white" strokeWidth={3} /> : <ChevronDown size={28} color="white" strokeWidth={3} />}
          </button>
        )}
      </div>

      {/* Stories Grid */}
      {isExpanded && (
        <div>
          {stories.length === 0 ? (
            <div
              style={{
                textAlign: 'center',
                padding: 'clamp(3rem, 6vw, 4rem) clamp(1.25rem, 3vw, 2rem)',
                background: 'linear-gradient(135deg, #F0F9FF 0%, #E0F2FE 100%)',
                borderRadius: '24px',
                border: '2px solid rgba(168, 216, 230, 0.4)',
                boxShadow: '0 8px 32px rgba(168, 216, 230, 0.15)',
              }}
            >
              <div style={{
                width: 'clamp(80px, 12vw, 100px)',
                height: 'clamp(80px, 12vw, 100px)',
                margin: '0 auto clamp(1rem, 3vw, 1.5rem)',
                background: 'linear-gradient(135deg, #3B82F6, #2563EB)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                animation: 'bounce 2s ease-in-out infinite',
                boxShadow: '0 12px 40px rgba(59, 130, 246, 0.3)',
              }}>
                <BookOpen size={48} color="white" strokeWidth={2.5} />
              </div>
              <h3 style={{
                background: 'linear-gradient(135deg, #1E40AF, #3B82F6)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                fontSize: 'clamp(1.25rem, 3vw, 1.75rem)',
                fontWeight: 900,
                marginBottom: 'clamp(0.5rem, 1vw, 0.75rem)',
              }}>
                Coming Soon!
              </h3>
              <p style={{
                color: '#1E40AF',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                fontWeight: 600,
                margin: 0,
                lineHeight: 1.6,
              }}>
                New <span style={{ fontWeight: 800 }}>learning stories</span> for this age category are being prepared.
              </p>
            </div>
          ) : (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                gap: '20px',
              }}
            >
              {stories.map((story) => (
                <LearningStoryCard
                  key={story.id}
                  id={story.id}
                  title={story.title}
                  prompt={story.prompt}
                  coverImageUrl={story.coverImageUrl}
                  difficultyLevel={story.difficultyLevel}
                  ageCategory={story.ageCategory}
                  learningTopic={story.learningTopic}
                  isCompleted={completionStatus[story.id] || false}
                  primaryLanguage={story.language}
                  secondaryLanguage={story.targetLanguage}
                />
              ))}
            </div>
          )}
        </div>
      )}

      <style jsx global>{`
        .age-icon:hover {
          transform: rotate(0deg) scale(1.05) !important;
          box-shadow: 0 20px 60px rgba(201, 182, 228, 0.5) !important;
        }
        
        @keyframes bounce {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-12px);
          }
        }
        
        @media (max-width: 768px) {
          .age-icon {
            width: clamp(64px, 15vw, 80px) !important;
            height: clamp(64px, 15vw, 80px) !important;
            font-size: clamp(2rem, 5vw, 2.5rem) !important;
          }
        }
      `}</style>
    </div>
  );
}

